/**
 * Answer Card Component
 * Handles answer input (free entry or multiple choice)
 */

import { useState, FormEvent, useRef, useEffect, RefObject } from 'react'
import { ANSWER_FONT_MAP } from '@config/constants'
import type { FontSize, Mode } from '../../../types'
import { useDynamicFontSize } from '@hooks/useDynamicFontSize'
import { useResponsiveFontPreset } from '@hooks/useResponsiveFontPreset'
import { useIsNarrow } from '@hooks/useIsNarrow'

interface DynamicOptionButtonProps {
  option: number | string
  fontSize: string
  onClick: () => void
  disabled: boolean
  contentKey: string
}

function DynamicOptionButton({
  option,
  fontSize,
  onClick,
  disabled,
  contentKey,
}: DynamicOptionButtonProps) {
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const dynamicFontSize = useDynamicFontSize({
    targetRef: buttonRef,
    contentKey,
    maxFontSize: fontSize,
    minFontSize: ANSWER_FONT_MAP.small,
  })

  return (
    <button
      ref={buttonRef}
      className="flashcard__option"
      style={{ fontSize: dynamicFontSize }}
      onClick={onClick}
      disabled={disabled}
    >
      {option}
    </button>
  )
}

interface AnswerCardProps {
  mode: Mode
  options?: (number | string)[]
  fontSize: FontSize
  onSubmit: (answer: string) => void
  onSkip: () => void
  showFeedback?: boolean
  isCorrect?: boolean
  disabled?: boolean
  isSpellingMode?: boolean
  listeningAvailable?: boolean
  listeningState?: 'idle' | 'recording' | 'transcribing' | 'success' | 'mismatch' | 'error'
  listeningMessage?: string
  heardTranscript?: string
  onStartListening?: () => void
  inputRef?: RefObject<HTMLInputElement>
  inputPlaceholder?: string
}

export default function AnswerCard({
  mode,
  options = [],
  fontSize,
  onSubmit,
  onSkip,
  showFeedback = false,
  isCorrect = false,
  disabled = false,
  isSpellingMode = false,
  listeningAvailable = false,
  listeningState = 'idle',
  listeningMessage = '',
  heardTranscript = '',
  onStartListening,
  inputRef,
  inputPlaceholder,
}: AnswerCardProps) {
  const [inputValue, setInputValue] = useState('')
  const formContainerRef = useRef<HTMLDivElement | null>(null)
  const responsiveFontSize = useResponsiveFontPreset({
    desiredSize: fontSize,
    sizeMap: ANSWER_FONT_MAP,
    containerRef: formContainerRef,
  })
  const fontSizeStyle = responsiveFontSize || ANSWER_FONT_MAP[fontSize] || ANSWER_FONT_MAP.large
  const isNarrow = useIsNarrow(1110)
  const isStacked = useIsNarrow(954)
  const [listeningCountdown, setListeningCountdown] = useState(6)

  useEffect(() => {
    if (listeningState !== 'recording') {
      setListeningCountdown(6)
      return
    }
    setListeningCountdown(6)
    const interval = window.setInterval(() => {
      setListeningCountdown(prev => (prev > 0 ? prev - 1 : 0))
    }, 1000)
    return () => window.clearInterval(interval)
  }, [listeningState])

  // Maintain focus on input in free mode when not disabled and not listening
  const shouldMaintainFocus = mode === 'free' && !disabled && !listeningAvailable
  useEffect(() => {
    if (!shouldMaintainFocus || !inputRef?.current) return

    // Focus immediately when becoming enabled
    inputRef.current.focus()

    // Use MutationObserver to detect DOM changes that might steal focus
    const observer = new MutationObserver(() => {
      // Re-focus input after DOM mutations if focus was lost
      // Check that focus went to body or nowhere (not to another interactive element)
      const active = document.activeElement
      const isLostFocus = !active || active === document.body || active.tagName === 'HTML'
      if (isLostFocus && inputRef.current && !inputRef.current.disabled) {
        inputRef.current.focus()
      }
    })

    // Observe the parent container for changes
    const container = inputRef.current.closest('.practice__container')
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
      })
    }

    return () => observer.disconnect()
  }, [shouldMaintainFocus, inputRef])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (inputValue.trim()) {
      onSubmit(inputValue.trim())
      setInputValue('')
      // Restore focus to input after submission
      setTimeout(() => {
        inputRef?.current?.focus()
      }, 50)
    }
  }

  const handleMultipleChoiceClick = (option: number | string) => {
    if (!disabled) {
      onSubmit(String(option))
    }
  }

  const listeningBusy = listeningState === 'recording' || listeningState === 'transcribing'

  const listeningPrimaryLabel = (() => {
    if (listeningState === 'recording') return 'Listening...'
    if (listeningState === 'transcribing') return 'Transcribing...'
    if (listeningState === 'success') return 'Marked correct'
    if (listeningState === 'mismatch') return 'Try again'
    if (listeningState === 'error') return 'Retry listening'
    return 'Start Listening'
  })()

  const renderListeningStatus = () => {
    if (!listeningAvailable) return null
    const hasTranscript = Boolean(heardTranscript)
    return (
      <div className="flashcard__listening">
        <div className="flashcard__listening-status">
          {listeningMessage || 'Mic ready. Tap to listen.'}
          {hasTranscript && (
            <span className="flashcard__listening-transcript">
              Heard: “{heardTranscript}”
            </span>
          )}
        </div>
      </div>
    )
  }

  // Free entry mode
  if (mode === 'free') {
    if (listeningAvailable) {
      const listeningLabel =
        listeningState === 'recording'
          ? `Listening${listeningCountdown ? ` (${listeningCountdown}s)` : ''}`
          : listeningState === 'transcribing'
            ? 'Transcribing...'
            : ''

      return (
        <div className="flashcard__answer" ref={formContainerRef}>
          <div
            className={
              isStacked
                ? 'flashcard__button-group flashcard__button-group--stacked'
                : 'flashcard__listening-stack'
            }
          >
            {isStacked ? (
              <button
                type="button"
                className={`flashcard__button flashcard__button--skip ${isStacked ? '' : 'flashcard__button--thin'} flashcard__button--skip-inline`}
                onClick={onSkip}
                disabled={disabled || listeningState === 'recording'}
              >
                Skip
              </button>
            ) : null}
            <button
              type="button"
              className={`flashcard__button flashcard__button--primary ${
                isStacked
                  ? 'flashcard__button--primary-wide'
                  : 'flashcard__button--listening'
              } ${
                listeningState === 'recording' ? 'flashcard__button--active' : ''
              }`}
              onClick={onStartListening}
              disabled={disabled}
            >
              <div className="flashcard__listening-content">
                <svg
                  className="flashcard__mic-icon"
                  width="48"
                  height="48"
                  viewBox="0 0 24 28"
                  role="presentation"
                  aria-hidden="true"
                >
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    d="M12 16a3 3 0 0 0 3-3V7a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3Z"
                  />
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    d="M6.5 12a5.5 5.5 0 0 0 11 0"
                  />
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    d="M12 21v-3"
                  />
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    d="M8.5 23.5h7"
                  />
                </svg>
                <div className="flashcard__listening-text">
                  <span className="flashcard__listening-primary">Tap to Speak</span>
                  {listeningLabel ? (
                    <span className="flashcard__listening-secondary">{listeningLabel}</span>
                  ) : null}
                </div>
              </div>
            </button>
            {!isStacked ? (
              <button
                type="button"
                className={`flashcard__button flashcard__button--skip flashcard__button--thin`}
                onClick={onSkip}
                disabled={disabled || listeningState === 'recording'}
              >
                Skip
              </button>
            ) : null}
          </div>
          {listeningMessage || heardTranscript ? (
            <div className="flashcard__listening flashcard__listening--compact">
              <div className="flashcard__listening-status">
                {listeningMessage}
                {heardTranscript ? (
                  <span className="flashcard__listening-transcript">Heard: “{heardTranscript}”</span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      )
    }

    return (
      <div className="flashcard__answer" ref={formContainerRef}>
        <form onSubmit={handleSubmit} className="flashcard__form">
          <label htmlFor="answer-input" className="flashcard__label">
            {isSpellingMode ? 'Spelling:' : 'Your Answer:'}
          </label>
          <input
            ref={inputRef}
            id="answer-input"
            name="practice-answer"
            type="text"
            className="flashcard__input"
            style={{ fontSize: fontSizeStyle }}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            disabled={disabled}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            data-form-type="other"
            placeholder={inputPlaceholder}
          />
          <div className="flashcard__button-group">
            <button
              type="button"
              className="flashcard__button flashcard__button--skip"
              onClick={onSkip}
              disabled={disabled}
            >
              Skip
            </button>
            {listeningAvailable ? (
              <button
                type="button"
                className="flashcard__button flashcard__button--primary"
                onClick={onStartListening}
                disabled={disabled || listeningBusy}
              >
                {listeningPrimaryLabel}
              </button>
            ) : (
              <button
                type="submit"
                className="flashcard__button flashcard__button--primary"
                disabled={disabled || !inputValue.trim()}
              >
                {isStacked ? 'Check Answer' : isNarrow ? 'Check' : 'Check Answer'}
              </button>
            )}
          </div>
          {renderListeningStatus()}
        </form>

        {showFeedback && (
          <div
            className={`flashcard__feedback flashcard__feedback--${isCorrect ? 'correct' : 'incorrect'}`}
            role="alert"
            aria-live="assertive"
          >
            {isCorrect ? '✓ Correct!' : '✗ Try again'}
          </div>
        )}
      </div>
    )
  }

  // Multiple choice mode
  return (
    <div className="flashcard__answer" ref={formContainerRef}>
      <div className="flashcard__options">
        {options.map((option, index) => (
          <DynamicOptionButton
            key={index}
            option={option}
            fontSize={fontSizeStyle}
            onClick={() => handleMultipleChoiceClick(option)}
            disabled={disabled}
            contentKey={`${option}-${index}`}
          />
        ))}
      </div>

      <div className="flashcard__button-group">
        <button
          type="button"
          className="flashcard__button flashcard__button--skip"
          onClick={onSkip}
          disabled={disabled}
        >
          Skip
        </button>
      </div>

      {renderListeningStatus()}

      {showFeedback && (
        <div
          className={`flashcard__feedback flashcard__feedback--${isCorrect ? 'correct' : 'incorrect'}`}
          role="alert"
          aria-live="assertive"
        >
          {isCorrect ? '✓ Correct!' : '✗ Try again'}
        </div>
      )}
    </div>
  )
}
